import { useState, useEffect, useContext } from 'react';
import { doc, onSnapshot, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

/**
 * Custom hook for managing user settings and pipelines
 */
export const useSettings = () => {
    const { currentUser } = useContext(AuthContext);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!currentUser?.uid) {
            setLoading(false);
            return;
        }

        const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');

        const unsubscribe = onSnapshot(
            settingsRef,
            async (docSnapshot) => {
                if (docSnapshot.exists()) {
                    setSettings(docSnapshot.data());
                } else {
                    console.log('Settings doc does not exist, initializing...');
                    const defaultPipeline = {
                        id: 'default',
                        name: 'Sales Pipeline',
                        isDefault: true,
                        stages: [
                            { position: 0, name: 'Contacts', color: '#6B7280' },
                            { position: 1, name: 'Proposal', color: '#3B82F6' },
                            { position: 2, name: 'Negotiation', color: '#F59E0B' },
                            { position: 3, name: 'Won', color: '#10B981' },
                            { position: 4, name: 'Lost', color: '#EF4444' },
                        ],
                        createdAt: new Date(),
                        updatedAt: new Date(),
                    };

                    const defaultSettings = {
                        gmailConnected: false,
                        gmailEmail: null,
                        gmailLabelName: 'EzyPipe',
                        gmailLabelId: null,
                        autoLogEmails: false,
                        emailTracking: false,
                        notifications: {
                            dealUpdates: true,
                            newEmails: true,
                        },
                        pipelines: [defaultPipeline],
                        updatedAt: new Date()
                    };

                    try {
                        await setDoc(settingsRef, defaultSettings);
                        setSettings(defaultSettings);
                    } catch (err) {
                        console.error('Error initializing settings:', err);
                    }
                }
                setLoading(false);
            },
            (err) => {
                console.error('Error listening to settings:', err);
                setError(err.message);
                setLoading(false);
            }
        );

        return unsubscribe;
    }, [currentUser?.uid]);

    const updatePipeline = async (pipelineId, updates) => {
        if (!currentUser?.uid || !settings) return;

        try {
            const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');

            const newPipelines = (settings.pipelines || []).map(p =>
                p.id === pipelineId ? { ...p, ...updates, updatedAt: new Date() } : p
            );

            await updateDoc(settingsRef, {
                pipelines: newPipelines,
                updatedAt: new Date()
            });

            toast.success('Pipeline updated successfully');
        } catch (err) {
            console.error('Error updating pipeline:', err);
            toast.error('Failed to update pipeline');
            throw err;
        }
    };

    return {
        settings,
        loading,
        error,
        updatePipeline
    };
};
